import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { createGrid, createRecordSource } from "../../rcsgrid";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CheckIcon from "@mui/icons-material/Check";
import React, { useEffect, useRef, useState } from "react";
import { MenuColumnsFilters } from "./MenuColumnsFilters";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import clsx from "clsx";
import menuStyles from "./filterMenuStyles.module.css";

interface Props {
  grid?: createGrid;
  source?: createRecordSource;
  visible: boolean;
}

interface MenuData {
  filterDataRef: { current: FilterData };
  grid?: createGrid;
  source?: createRecordSource;
}

interface FilterData {
  hFilterVisible: boolean;
  hFilterState: boolean;
}

export interface FilterSubMenuItem {
  name: string;
  value: "CFilterClear";
  key: string;
}

interface FilterMenuItem {
  name: string;
  value?: string;
  showIcon?: boolean;
  icon?: React.JSX.Element;
  disabled?: boolean;
  values?: Array<FilterSubMenuItem>;
  key?: string;
}

/**
 * Возвращает объект с данными для меню
 *
 *
 * @param {Object} [data] объект с данными таблицы
 * @param {Object} [data.filterDataRef] ссылка на объект с состоянием горизонтального фильтра
 * @param {createGrid} [data.grid] ссылка на экземпляр класса таблицы
 * @param {createRecordSource} [data.sorce] ссылка на экземпляр класса данных таблицы
 *
 * @returns Возвращает массив с данными для каждого элемента меню
 */
const getMenu = (data: MenuData): Array<FilterMenuItem> => {
  const { grid, source, filterDataRef } = data;
  const filterData = grid?.getFilterData();
  filterDataRef.current.hFilterState = Boolean(filterData?.rowFilterFiltered);
  filterDataRef.current.hFilterVisible = Boolean(grid?.filter.style.display === "grid");
  const filterValues = Object.entries(filterData || {}).reduce((acc: Array<FilterMenuItem>, [key, value]) => {
    const title = source?.getFieldTitle(key);
    if (value.filtered) acc.push({ name: title, value: "CFilterClear", key: key });
    return acc;
  }, []);

  return [
    {
      name: "Видимость горизонтального фильтра",
      value: "HFilterVisible",
      showIcon: filterDataRef.current.hFilterVisible,
      icon: <CheckIcon className={clsx(menuStyles.menuIcon)} />,
    },
    {
      name: filterDataRef.current.hFilterState ? "Выключить горизонтальный фильтр" : "Включить горизонтальный фильтр",
      value: "HFilter",
      showIcon: true,
      icon: (
        <>
          {filterDataRef.current.hFilterState ? (
            <FilterAltIcon className={clsx(menuStyles.menuGroupIconLeft)} />
          ) : (
            <FilterAltOffIcon className={clsx(menuStyles.menuGroupIconLeft)} />
          )}
          <DragHandleIcon className={clsx(menuStyles.menuGroupIconRight)} />
        </>
      ),
      disabled: !filterDataRef.current.hFilterVisible,
    },
    {
      name: "Очистить горизонтальный фильтр",
      value: "HFilterClear",
      showIcon: true,
      icon: (
        <>
          <FilterAltOffIcon className={clsx(menuStyles.menuGroupIconLeft)} />
          <DragHandleIcon className={clsx(menuStyles.menuGroupIconRight)} />
        </>
      ),
      disabled: !filterDataRef.current.hFilterState,
    },
    { name: "Снять фильтры с", values: filterValues, disabled: filterValues.length === 0 },
    {
      name: "Снять все фильтры на колонках",
      value: "CFiltersClear",
      disabled: filterValues.length === 0,
    },
    {
      name: "Снять все фильтры",
      value: "AllFiltersClear",
      showIcon: true,
      icon: <FilterAltOffIcon className={clsx(menuStyles.menuIcon)} />,
    },
    {
      name: "Отфильтровать по текущему значению",
      value: "HFilterCurrentValue",
    },
  ];
};

/**
 * Компонент управления фильтрацией таблицы
 *
 * Поля компонента:
 *    1.[openFilterMenu, setOpenFilterMenu] - состояние открытия меню
 *    2.[menuState, setMenuState] - текущие данные меню
 *    3.[menu, setMenu] - массив с React.JSX.Element для отрисовки меню
 *    4.filterAnchorEl - якорь для позиционирования меню
 *    5.filterData - объект с состоянием горизонтального фильтра
 *
 * @param {Object} [props] данные для меню
 * @param {Object} [props.grid] ссылка на экземпляр класса таблицы
 * @param {Object} [props.source] ссылка на экземпляр класса данных таблицы
 * @param {Object} [props.visible] видимость кнопки открытия меню
 *
 * @returns Возвращает компонент для управления фильтрацией с помощью выпадающего меню
 */
export default function FilterMenuForButtonGridFilter(props: Props): React.JSX.Element {
  const { grid, source, visible } = props;

  const [openFilterMenu, setOpenFilterMenu] = useState(false);

  const [menuState, setMenuState] = useState<Array<FilterMenuItem>>([]);
  const [menu, setMenu] = useState<Array<React.JSX.Element>>();

  const filterAnchorEl = useRef<any>(null);

  const filterData = useRef<FilterData>({
    hFilterVisible: false,
    hFilterState: false,
  });

  /**
   * Инициализация видимости горизонтального фильтра в зависимости от его состояния в таблице
   */
  const initVisible = () => {
    if (grid && source) {
      filterData.current.hFilterVisible = grid.filter.style.display === "grid";
    }
  };
  useEffect(initVisible, [grid, source]);

  /**
   * Обновление элементов списка при изменении объекта с данными
   */
  const updateJsx = () => {
    if (menuState) {
      setMenu(getJsxMenu(menuState));
    }
  };
  useEffect(updateJsx, [menuState]);

  /**
   * Возвращает массив JSX-елементов для меню
   *
   * @param {Array} [items] массив с данными для элементов
   * @returns Возвращает список элементов для меню
   */
  const getJsxMenu = (items: Array<FilterMenuItem>): Array<React.JSX.Element> => {
    return items.map((item: FilterMenuItem, index: number) => {
      const { value, name, showIcon, icon, disabled, values } = item;
      return (
        <MenuItem
          disabled={disabled}
          onClick={(ev) => {
            menuClickHandler(ev, value);
          }}
          className={clsx(menuStyles.menuItem)}
          key={"ButtonsMenu" + index}
        >
          {values ? (
            <MenuColumnsFilters values={values} onClickMenuItem={menuClickHandler} name={name} />
          ) : (
            <>
              {showIcon && icon}
              <ListItemText className={clsx(menuStyles.menuItemLabel)}>{name}</ListItemText>
            </>
          )}
        </MenuItem>
      );
    });
  };

  /**
   * Обновляет данные для меню и переключает его отображение при клике на кнопку фильтрации
   */
  const clickFilterButton = (ev: any) => {
    ev.preventDefault();
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
    setOpenFilterMenu(!openFilterMenu);
  };

  /**
   * Обробатывает клики на элементы списка
   *
   * @param ev событие клика
   * @param comand команда привязанная к элементу меню
   * @param items данные выбранных элементов
   */
  const menuClickHandler = (ev: any, comand?: string, items?: { [key: string]: FilterMenuItem }) => {
    if (!comand) return;

    const comands: { [key: string]: (items?: { [key: string]: FilterMenuItem }) => Promise<void> | void } = {
      HFilterVisible: changeHFilterVisible,
      HFilter: switchHFilter,
      HFilterClear: clearHFilter,
      CFilterClear: clearCFilter,
      CFiltersClear: clearAllCFilters,
      AllFiltersClear: clearAllFilters,
      HFilterCurrentValue: setHFilterCurrentValue,
    };
    comands[comand]?.(items);
  };

  /**
   * Переключает видимость горизонтального фильтра
   */
  const changeHFilterVisible = async () => {
    await grid?.setOptions(1);
    filterData.current.hFilterVisible = !filterData.current.hFilterVisible;
    await grid?.switchFilter(filterData.current.hFilterVisible);
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
  };

  /**
   * Переключает фильтрацию (вкл/выкл) горизонтального фильтра
   */
  const switchHFilter = async () => {
    const filterButton = grid?.filter?.querySelector("button");
    filterButton?.click();
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
  };

  /**
   * Очищает все значения только горизонтального фильтра
   */
  const clearHFilter = async () => {
    const data = grid?.getFilterData();

    if (data) {
      data.rowFilter = {};
    }
    grid?.setFilterData({ ...data });

    await grid?.applyColumnFilter(getFilterResult(data || {}, { column: true }));
    await grid?.refreshSource("!");
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
  };

  /**
   * Очищает фильтр на колонке по указанным ключам
   *
   * @param {Object}[items] элементы с данными об отфильтрованных колонках
   */
  const clearCFilter = async (items?: { [key: string]: FilterMenuItem }) => {
    const keys = Object.keys(items || {});
    const newItem = { checkData: {}, filtered: undefined, focusRecID: undefined, multiCheck: false };
    const newFilterData = keys.reduce((acc: { [key: string]: {} }, key) => {
      acc[key] = newItem;
      return acc;
    }, {});
    const data = grid?.getFilterData();
    grid?.setFilterData({ ...data, ...newFilterData });
    await grid?.applyColumnFilter(getFilterResult(data || {}, { column: true, row: true, ignor: keys }));
    await grid?.refreshSource("!");
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
  };

  /**
   * Очищает фильтры со всех отфильтрованных колонок
   */
  const clearAllCFilters = async () => {
    const data = grid?.getFilterData();
    grid?.setFilterData({ rowFilter: data?.rowFilter || {}, rowFilterFiltered: data?.rowFilterFiltered || false });
    await grid?.applyColumnFilter(getFilterResult(data || {}, { row: true }));
    await grid?.refreshSource("!");
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
  };

  /**
   * Очищает все фильтры
   */
  const clearAllFilters = async () => {
    grid?.setFilterData({});
    await grid?.applyColumnFilter({});
    await grid?.refreshSource("!");
    if (filterData.current.hFilterVisible) {
      await grid?.setOptions(1);
      filterData.current.hFilterVisible = !filterData.current.hFilterVisible;
    }
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
  };

  /**
   *  Устанавливает фильтр по значению из текущей ячейки
   */
  const setHFilterCurrentValue = async () => {
    const key = grid?.getCurrentFieldName();
    if (!key) return;
    const text = source?.getFieldTextSync(key);
    if (!text) return;
    const data = grid?.getFilterData();
    if (!filterData.current.hFilterVisible) {
      filterData.current.hFilterVisible = !filterData.current.hFilterVisible;
      await grid?.setOptions(1);
      await grid?.switchFilter(filterData.current.hFilterVisible);
    }
    if (data) {
      if (!data.rowFilter) data.rowFilter = {};
      data.rowFilterFiltered = true;
      data.rowFilter[key] = { [text]: null, ["$tag"]: 1 };
      if (data?.[key]) delete data?.[key];
      grid?.setFilterData({ ...data });
      await grid?.applyColumnFilter(getFilterResult(data || {}, { row: true, column: true }));
      await grid?.refreshSource("!");
    }
    setMenuState([...getMenu({ filterDataRef: filterData, grid, source })]);
  };

  /**
   * Возвращает объект с данными для изменной фильтрации по параметрам
   *
   * @param {Object} [data] - изначальный объект с данными
   * @param {Object} [params] - параметры для изменения объекта фильтрации
   * @param {boolean} [params.row] - сохранить данные только горизонтального фильтра
   * @param {boolean} [params.column] - сохранить данные только фильтров на колонках
   * @param {Array<string>} [params.ignor] - колонки которые не войдут в итоговый объект фильтрации
   * @returns возвращает отфильтрованный объект по выбранным параметрам
   */
  const getFilterResult = (
    data: { [key: string]: any },
    params: { row?: boolean; column?: boolean; ignor?: Array<string> },
  ): { [key: string]: any } => {
    if (params.row && !params.column) {
      if (data.rowFilterFiltered) {
        return { ...data?.rowFilter };
      }
    }
    if (params.column && !params.row) {
      const filterResult: { [key: string]: string } = {};
      Object.entries(data || {}).forEach(([key, filter]) => {
        if (!(data?.rowFilterFiltered && key === "rowFilter")) {
          if (filter.filtered && params.ignor?.includes(key) !== true) filterResult[key] = filter.value;
        }
      });
      return filterResult;
    }

    if (params.column && params.row) {
      return {
        ...getFilterResult(data, { row: true }),
        ...getFilterResult(data, { column: true, ignor: params.ignor }),
      };
    }

    return {};
  };

  return (
    <>
      <Tooltip title="Фильтрация документа">
        <span>
          <IconButton
            onContextMenu={clickFilterButton}
            ref={filterAnchorEl}
            className={clsx(!visible && menuStyles.menuHidden)}
            onClick={changeHFilterVisible}
          >
            {filterData.current.hFilterVisible ? (
              <FilterAltIcon sx={{ fontSize: "1.15rem" }} />
            ) : (
              <FilterAltOffIcon sx={{ fontSize: "1.15rem" }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={filterAnchorEl.current}
        open={openFilterMenu}
        onClose={() => {
          setOpenFilterMenu(false);
        }}
      >
        {menu}
      </Menu>
    </>
  );
}
